<template>
    <div
        v-if="options"
        class="my-select relative group flex items-center min-h-12 min-w-12"
        :class="{
            'has-value': hasValue,
            'is-focus': is_focus,
            'has-error': error
        }"
    >
        <multiselect
            v-model="current_value"
            :options="options"
            ref="select"
            :name="name"
            placeholder="Seleziona un elemento"
            :track-by="trackBy"
            :label="label"
            :disabled="disabled"
            :searchable="searchable"
            :class="additionalClass"
            :multiple="multiple"
            :open-direction="openDirection"
            :allow-empty="allowEmpty"
            @update:model-value="onInput"
            @open="onFocus"
            @close="onBlur"
        >
            <template #option="props">
                <slot name="option" :option="props.option"></slot>
            </template>

            <template #noOptions>
                Nessun elemento selezionabile
            </template>

            <!-- <template #selection="props">
                <template v-if="multiple">
                    {{ props }}
                    <span
                        v-for="value in values"
                        :key="value"
                    >
                        {{ value.id || value }}
                    </span>
                </template>
            </template> -->
        </multiselect>

        <div
            class="absolute z-40 opacity-60 top-3 left-3 text-grey transition-all select-none group-[.has-value]:top-1 group-[.has-value]:text-xs group-[.has-value]:h-auto group-[.has-value]:leading-4"
            @click="$refs.select.activate()"
        >
            {{ placeholder }}
        </div>

        <!-- <template v-if="clearable && hasValue">
            <button
                class="absolute top-1 right-2 bg-transparent text-xs px-1 focus:outline-none"
                @click.stop.prevent="clearInput"
            >
                <img
                    src="/img/icons/clear.svg"
                    class="w-4 absolute t-3 r-5"
                    alt="Reset"
                />
            </button>
        </template> -->

        <span v-if="error" class="left-0 top-12 text-red md:absolute">
            {{ error }}
        </span>
    </div>
</template>

<script>
import 'vue-multiselect/dist/vue-multiselect.css'

import { watch } from 'vue'
import { ref } from '@vue/reactivity'
import { computed, onMounted } from '@vue/runtime-core'

import Multiselect from 'vue-multiselect'

export default {
    components: {
        Multiselect,
    },

    props: {
        additionalClass: {
            type: String,
            required: false,
        },
        allowEmpty: {
            type: Boolean,
            default: true,
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        error: {
            type: String,
            default: null,
        },
        label: {
            type: String,
            required: false,
        },
        modelValue: {
            type: [String, Number, Object],
            required: false,
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        name: {
            type: String,
            required: false,
        },
        openDirection: {
            type: String,
            required: false,
        },
        options: {
            type: Array,
            default: [],
        },
        placeholder: {
            type: String,
            required: false,
        },
        searchable: {
            type: Boolean,
            default: false,
        },
        trackBy: {
            type: String,
            required: false,
        },
    },

    emits: ['focus', 'blur', 'update:modelValue'],

    setup(props, ctx) {
        const current_value = ref(props.modelValue)
        const current_placeholder = ref(props.placeholder)
        const is_focus = ref(false)

        const hasValue = computed(() => {
            const wrong_values = ['', null, undefined]

            return ! wrong_values.includes(current_value.value)
        })

        const onInput = selectValue => {
            checkFocus()

            let value = current_value.value || []

            if(typeof value === 'object' && value) {
                value = value[props.trackBy] ? value[props.trackBy] : null
            }

            if(Array.isArray(selectValue) && selectValue.length) {
                value = selectValue
            }

            if(! value) {
                // value = []
                value = null
            }

            ctx.emit('update:modelValue', value)
        }

        const setCurrentValue = () => {
            if(
                props.trackBy
                && typeof current_value.value !== 'object'
                && current_value.value && props.options && props.options.length
            ) {
                current_value.value = props.options.find(option => option[props.trackBy] == current_value.value)
            }
        }

        const onFocus = () => {
            is_focus.value = true
            current_placeholder.value = ''

            // ctx.emit('focus')
        }

        const onBlur = () => {
            is_focus.value = false

            ctx.emit('blur')
        }

        const checkFocus = () => {
            if(! current_value.value) {
                is_focus.value = false
            }
        }

        const clearInput = () => {
            current_value.value = null

            ctx.emit('update:modelValue', current_value.value)
        }

        onMounted(() => {
            setCurrentValue()
        })

        watch(
            () => props.options,
            () => {
                setCurrentValue()
            }
        )

        watch(
            () => props.modelValue,
            (newValue, oldValue) => {
                current_value.value = props.modelValue

                setCurrentValue()
            }
        )

        return {
            current_value,
            current_placeholder,
            is_focus,
            hasValue,
            setCurrentValue,
            onInput,
            onFocus,
            onBlur,
            clearInput,
        }
    },
}
</script>

<style>
.my-select {
    @apply md:min-w-0 w-full;
}
.my-select .multiselect__tags {
    min-height: 48px;
    @apply p-0 pl-3 border-none rounded;
}

.my-select .multiselect__select {
    @apply z-10;
}

.my-select .multiselect__placeholder {
    @apply hidden;
}

.my-select .multiselect__input {
    @apply relative !top-4;
}

.my-select.has-value .multiselect__single {
    @apply pt-3 pl-0 h-9 leading-9;
}

.my-select .multiselect__content-wrapper {
    @apply z-50;
}

.my-select .multiselect__content-wrapper .multiselect__option--highlight {
    @apply font-bold bg-purple-light;
}

.my-select .multiselect__content-wrapper .multiselect__option--selected.multiselect__option--highlight {
    @apply bg-red;
}

.my-select .multiselect__content-wrapper .multiselect__option--highlight:after,
.my-select .multiselect__content-wrapper .multiselect__option--selected:after {
    content: '';
}

.my-select .multiselect__tags-wrap {
    display: flex;
    flex-wrap: wrap;
    padding-top: 1.25rem;
    margin-right: 2rem;
}

.my-select .multiselect__tag {
    @apply bg-purple-lighter;
}

.my-select .multiselect__tag-icon:after {
    @apply text-white;
}
</style>
